import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { ArrowLeft, ClipboardList, Clock } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLOR = {
  solicitada: "bg-slate-100 text-slate-700",
  em_analise: "bg-blue-50 text-blue-700",
  aguardando_profissional: "bg-amber-50 text-amber-700",
  confirmada: "bg-emerald-50 text-emerald-700",
  realizada: "bg-emerald-100 text-emerald-800",
  cancelada: "bg-red-50 text-red-700",
  nao_realizada: "bg-red-50 text-red-700",
};

export function FPRequestList() {
  const [reqs, setReqs] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/fp/requests").then((r) => setReqs(r.data)).finally(() => setLoading(false));
  }, []);

  const groups = {
    open: reqs.filter((r) => ["solicitada", "em_analise", "aguardando_profissional"].includes(r.status)),
    confirmed: reqs.filter((r) => r.status === "confirmada"),
    done: reqs.filter((r) => r.status === "realizada"),
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8" data-testid="fp-req-list">
      <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#0B2B62] mb-6">Meus atendimentos</h1>

      {loading ? <div className="text-slate-500">Carregando…</div>
        : reqs.length === 0 ? (
          <div className="bg-white border-2 border-dashed border-slate-300 rounded-xl p-10 text-center">
            <ClipboardList className="w-10 h-10 text-slate-400 mx-auto mb-3" />
            <div className="text-slate-700">Nenhuma solicitação criada ainda.</div>
            <Link to="/filho-presente/solicitar-consulta" className="inline-block mt-4 bg-[#0B2B62] text-white px-4 py-2 rounded-lg text-sm">Solicitar consulta</Link>
          </div>
        ) : (
          <div className="space-y-6">
            <Section title="Abertas" items={groups.open} />
            <Section title="Confirmadas" items={groups.confirmed} />
            <Section title="Concluídas" items={groups.done} />
          </div>
        )}
    </div>
  );
}

const Section = ({ title, items }) => (
  items.length === 0 ? null : (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold mb-3">{title} · {items.length}</div>
      <div className="bg-white border border-slate-200 rounded-xl divide-y">
        {items.map((r) => (
          <Link key={r.id} to={`/filho-presente/solicitacoes/${r.id}`}
            data-testid={`fp-req-item-${r.id}`}
            className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-slate-50">
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">
                {r.type === "consulta" ? `Consulta · ${r.specialty || "—"}`
                  : r.type === "exame" ? `Exame · ${r.exam_type}`
                  : r.type === "transporte" ? "Transporte"
                  : r.type === "acompanhante" ? "Acompanhante" : r.type}
              </div>
              <div className="text-xs text-slate-500 truncate">
                {r.beneficiary_name} · {r.desired_date || "sem data"}
                {r.priority === "urgente" && <span className="ml-2 text-red-600 font-bold">URGENTE</span>}
              </div>
            </div>
            <span className={`text-xs px-2 py-1 rounded-full shrink-0 ${STATUS_COLOR[r.status] || "bg-slate-100 text-slate-700"}`}>
              {r.status?.replace("_", " ")}
            </span>
          </Link>
        ))}
      </div>
    </div>
  )
);

export function FPRequestDetail() {
  const { id } = useParams();
  const [r, setR] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => api.get(`/fp/requests/${id}`).then((x) => setR(x.data)).finally(() => setLoading(false));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const cancel = async () => {
    if (!window.confirm("Cancelar esta solicitação?")) return;
    try {
      await api.patch(`/fp/requests/${id}/status`, { status: "cancelada", note: "Cancelada pela família" });
      toast.success("Solicitação cancelada");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  if (loading) return <div className="p-10 text-slate-500">Carregando…</div>;
  if (!r) return <div className="p-10 text-red-500">Não encontrada</div>;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8" data-testid="fp-req-detail">
      <Link to="/filho-presente/solicitacoes" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#0B2B62] mb-4">
        <ArrowLeft className="w-4 h-4" /> Meus atendimentos
      </Link>

      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">
              {r.type === "consulta" ? "Consulta" : r.type === "exame" ? "Exame" : r.type}
            </div>
            <h1 className="font-display text-2xl font-bold text-[#0B2B62] mt-1">
              {r.specialty || r.exam_type || "Solicitação"}
            </h1>
            <div className="text-slate-600 text-sm mt-1">
              {r.beneficiary_name} · {r.desired_date}{r.desired_time && ` ${r.desired_time}`}
            </div>
          </div>
          <span className={`text-xs px-3 py-1.5 rounded-full ${STATUS_COLOR[r.status]}`}>
            {r.status?.replace("_", " ")}
          </span>
        </div>

        {r.notes && (
          <div className="mt-4 bg-slate-50 rounded-lg p-3 text-sm text-slate-700 whitespace-pre-line">{r.notes}</div>
        )}

        <div className="mt-6">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold mb-3">Timeline</div>
          <ol className="relative border-l-2 border-slate-200 ml-2 space-y-4">
            {(r.timeline || []).map((t, i) => (
              <li key={i} className="ml-4">
                <div className="absolute -left-[7px] w-3 h-3 rounded-full bg-[#0B2B62]" />
                <div className="text-sm font-medium">{t.label || t.step}</div>
                <div className="text-xs text-slate-500 inline-flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(t.at).toLocaleString("pt-BR")}{t.by && ` · ${t.by}`}</div>
              </li>
            ))}
          </ol>
        </div>

        {["solicitada", "em_analise", "aguardando_profissional"].includes(r.status) && (
          <div className="mt-6 pt-4 border-t">
            <button onClick={cancel} data-testid="fp-cancel-req"
              className="text-sm text-red-600 hover:underline">Cancelar solicitação</button>
          </div>
        )}
      </div>
    </div>
  );
}
